// src/pages/ResetPassword.js
//
// Handles the link from a password reset email — reads the oobCode from
// the URL, verifies it, then lets the person set a new password, all on
// app.techbugz.com's own branded page instead of Firebase's default
// {project-id}.firebaseapp.com template. Public route, no login required
// (that's the whole point — this IS how you get back in).
import React, { useState, useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { verifyPasswordResetCode, confirmPasswordReset } from "firebase/auth";
import { auth } from "../lib/firebase";
import { Lock, CheckCircle2, XCircle } from "lucide-react";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const oobCode = searchParams.get("oobCode");

  const [status, setStatus] = useState("verifying"); // verifying | ready | invalid | success
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!oobCode) { setStatus("invalid"); return; }
    verifyPasswordResetCode(auth, oobCode)
      .then(addr => { setEmail(addr); setStatus("ready"); })
      .catch(() => setStatus("invalid"));
  }, [oobCode]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setStatus("success");
    } catch (err) {
      setError("That link has expired or already been used — request a new one.");
    } finally {
      setSubmitting(false);
    }
  }

  useEffect(() => {
    if (status !== "success") return;
    const t = setTimeout(() => navigate("/login"), 2000);
    return () => clearTimeout(t);
  }, [status, navigate]);

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg)", padding: 24 }}>
      <div style={{ width: "100%", maxWidth: 380, textAlign: "center" }}>
        <img src="/logo.png" alt="Tech Bugz" style={{ height: 36, objectFit: "contain", marginBottom: 28 }} />

        {status === "verifying" && (
          <p style={{ fontSize: 14, color: "var(--text-3)" }}>Verifying your link…</p>
        )}

        {status === "invalid" && (
          <>
            <XCircle size={36} color="var(--red)" style={{ marginBottom: 14 }} />
            <h1 style={{ fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Link expired or invalid</h1>
            <p style={{ fontSize: 13.5, color: "var(--text-3)", marginBottom: 20 }}>
              This password reset link is no longer valid. Ask whoever invited you to send a new one, or contact <a href="mailto:service@techbugz.com">service@techbugz.com</a>.
            </p>
            <Link to="/login" style={{ fontSize: 13.5, fontWeight: 600, color: "var(--accent)", textDecoration: "none" }}>Back to login</Link>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle2 size={36} color="var(--green)" style={{ marginBottom: 14 }} />
            <h1 style={{ fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Password set</h1>
            <p style={{ fontSize: 13.5, color: "var(--text-3)", marginBottom: 20 }}>Taking you to login…</p>
            <button
              onClick={() => navigate("/login")}
              style={{ padding: "11px 28px", borderRadius: 99, border: "none", background: "var(--accent)", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
            >
              Go to login
            </button>
          </>
        )}

        {status === "ready" && (
          <>
            <div style={{
              width: 48, height: 48, borderRadius: 14, background: "var(--accent-lt)",
              display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px",
            }}>
              <Lock size={20} color="var(--accent)" />
            </div>
            <h1 style={{ fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 800, marginBottom: 4 }}>Set a new password</h1>
            <p style={{ fontSize: 13, color: "var(--text-3)", marginBottom: 22 }}>for <strong>{email}</strong></p>

            <form onSubmit={handleSubmit} style={{ textAlign: "left" }}>
              {error && (
                <div style={{ background: "var(--red-lt)", border: "1px solid rgba(239,68,68,.3)", borderRadius: "var(--radius)", padding: "11px 14px", marginBottom: 14, color: "#f87171", fontSize: 13 }}>
                  {error}
                </div>
              )}
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "var(--text-2)", marginBottom: 6 }}>New password</label>
                <input
                  type="password" required value={password} onChange={e => setPassword(e.target.value)}
                  style={{ width: "100%", padding: "11px 14px", border: "1px solid var(--border-2)", borderRadius: "var(--radius)", fontSize: 14, background: "var(--surface-4)", color: "var(--text-1)", outline: "none" }}
                />
              </div>
              <div style={{ marginBottom: 22 }}>
                <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "var(--text-2)", marginBottom: 6 }}>Confirm password</label>
                <input
                  type="password" required value={confirm} onChange={e => setConfirm(e.target.value)}
                  style={{ width: "100%", padding: "11px 14px", border: "1px solid var(--border-2)", borderRadius: "var(--radius)", fontSize: 14, background: "var(--surface-4)", color: "var(--text-1)", outline: "none" }}
                />
              </div>
              <button type="submit" disabled={submitting} style={{
                width: "100%", padding: "12px 24px", borderRadius: "var(--radius)", border: "none",
                background: submitting ? "rgba(0,113,235,.4)" : "var(--accent)", color: "#fff",
                fontSize: 14.5, fontWeight: 700, cursor: submitting ? "not-allowed" : "pointer", fontFamily: "inherit",
              }}>
                {submitting ? "Setting password…" : "Set password"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
